import React, { useState } from 'react'
import cx from 'classnames'
import styles from './Hero.module.scss'
import { Col, Container, Row } from 'react-grid-system'
import { Heading, Paragraph, Vimeo, Breadcrumb } from '~elements'
import Image from 'next/image'
import { AssetType, ButtonProps, RichTextType, VimeoProps } from '~types'
import { Button } from '~elements'
import { isComponentOfType } from 'utils'
import { useWindowSize } from 'utils/hooks'
import { NEXT_IMAGE_QUALITY, XO_PATHNAME_TO_DISPLAYNAME } from '~constants'

interface HeroProps {
  backgroundImage?: AssetType
  ctaSection?: Array<ButtonProps>
  description: RichTextType
  detailSection?: VimeoProps
  eyebrow?: string
  displayBreadcrumb?: boolean
  subTitle?: string
  subheadline: string
  title: string
  readMore?: RichTextType
  isGalleryComponent?: boolean
  logo?: AssetType
}

const Hero = ({
  backgroundImage,
  ctaSection,
  description,
  detailSection,
  eyebrow,
  displayBreadcrumb,
  readMore,
  title,
  subTitle = '',
  subheadline = '',
  isGalleryComponent,
  logo
}: HeroProps) => {
  const [readMoreIsOpen, setReadMoreIsOpen] = useState(false)
  const { isBreakpoint: isBelowBreakpointMd } = useWindowSize('md', '<')
  const toggleReadMore = () => setReadMoreIsOpen(!readMoreIsOpen)
  const VimeoComponent = () =>
    detailSection && detailSection.url && detailSection.posterImage && <Vimeo {...detailSection} />
  const textJustification = logo?.url ? 'left' : 'center'
  const H1Heading = (
    <Heading level={1} justify={textJustification}>
      {title}
    </Heading>
  )

  return (
    <>
      <div className={cx([styles['hero-container'], styles[logo ? 'has-logo' : '']])}>
        {displayBreadcrumb && (
          <div className={styles['breadcrumb-container']}>
            <Breadcrumb pathNameToDisplayName={XO_PATHNAME_TO_DISPLAYNAME} />
          </div>
        )}
        <div className={styles['hero']}>
          <Container
            className={'container container--half-padding-xs'}
            // zIndex so it overlaps absolutely positioned gradient
            style={{ zIndex: 2 }}
          >
            <Row>
              <Col xl={detailSection && detailSection.url ? 7 : 12} lg={detailSection && detailSection.url ? 6 : 12}>
                {eyebrow && (
                  <Heading level={6} justify={textJustification}>
                    {eyebrow}
                  </Heading>
                )}
                {title && logo?.url ? (
                  <div className={styles['title-with-logo']}>
                    {H1Heading}
                    <img className={styles.logo} src={logo.url} alt={logo.alt || ''} />
                  </div>
                ) : title ? (
                  H1Heading
                ) : null}
                {subTitle && (
                  <Heading level={2} justify={textJustification}>
                    {subTitle}
                  </Heading>
                )}
                {subheadline && (
                  <Paragraph text={{ html: `<p>${subheadline}</p>` }} justify={textJustification} color='light' />
                )}
                {description && (
                  <div className={styles['description-container']}>
                    <Paragraph
                      text={description}
                      fontSize='lg'
                      justify={textJustification}
                      color='light'
                      noMarginBottom
                    />
                  </div>
                )}
              </Col>
              {detailSection && !isBelowBreakpointMd && (
                <Col xl={5} lg={6}>
                  <div className={styles['details-container']}>{VimeoComponent()}</div>
                </Col>
              )}
            </Row>
            {ctaSection &&
              ctaSection.length > 0 &&
              ctaSection.map(button => {
                // @TODO: need to restrict ctaSection to Button only
                if (!isComponentOfType<ButtonProps>(button, 'Button')) return null

                return <Button {...button} justify={textJustification} analytics={{ linkModule: 'hero' }} />
              })}
          </Container>
        </div>

        {readMore && readMore?.html !== '<p></p>' && description && (
          <Container className='container'>
            <Row>
              <Col xl={7} lg={6}>
                <button onClick={toggleReadMore} className={styles['read-or-hide']}>
                  {readMoreIsOpen ? 'Read less' : 'Read more'}
                  <div className={cx([styles['caret'], styles[readMoreIsOpen ? '' : 'is-open']])} />
                </button>
                {readMoreIsOpen ? (
                  <div className={styles['read-more-content']}>
                    <Paragraph text={readMore} justify={textJustification} color='light' />
                  </div>
                ) : null}
              </Col>
            </Row>
          </Container>
        )}

        {detailSection && isBelowBreakpointMd && (
          <Container className={'container'}>
            <div className={styles['details-container']}>{VimeoComponent()}</div>
          </Container>
        )}

        {backgroundImage?.url && (
          <Image
            alt={''}
            src={backgroundImage.url}
            fill
            style={{
              opacity: `${isGalleryComponent || logo ? '1' : '.3'}`,
              objectFit: 'cover',
              objectPosition: 'top'
            }}
            quality={NEXT_IMAGE_QUALITY}
          />
        )}
      </div>
    </>
  )
}

export default Hero
